import { httpsCallable } from 'firebase/functions';
import {
  addDoc,
  collection,
  doc,
  increment,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import axios from 'axios';

import { auth, db, functions, storage } from '../firebaseClient';
import firebaseConfig from '../../firebase';
import { getApiBaseUrl } from '../../utils/backendBaseUrl';

export type InboxResult = { conversations: any[] };
export type MessagesResult = { conversation: any; messages: any[] };

export type DirectConversationResult = { conversationId: string };

function requireUid(): string {
  const uid = auth?.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated');
  return uid;
}

async function ensureFreshIdToken(): Promise<void> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  // Forza un token valido (riduce i 401 su callable quando l'auth è appena inizializzata)
  await u.getIdToken(true);
}

async function callOnCallFunctionFallback<T>(name: string, payload: Record<string, any>): Promise<T> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  const token = await u.getIdToken();

  const projectId = String((firebaseConfig as any)?.projectId || '').trim();
  if (!projectId) throw new Error('Missing firebase projectId');

  const url = `https://europe-west1-${projectId}.cloudfunctions.net/${name}`;
  try {
    const res = await axios.post(
      url,
      { data: payload },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const json = res?.data;
    return (json?.result ?? json?.data ?? json) as T;
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const message =
      data?.error?.message ||
      data?.error?.status ||
      data?.detail ||
      e?.message ||
      (status ? `HTTP ${status}` : 'Request failed');
    throw new Error(String(message));
  }
}

async function callBackendDmGetOrCreate(payload: Record<string, any>): Promise<DirectConversationResult> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  const token = await u.getIdToken();

  const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  const url = `${apiBase}/community/dm/get-or-create`;

  try {
    const res = await axios.post(
      url,
      payload || {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return res.data as DirectConversationResult;
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const message = data?.detail || data?.error?.message || data?.error?.status || e?.message || (status ? `HTTP ${status}` : 'Request failed');
    throw new Error(String(message));
  }
}

async function callBackendDmInbox(payload: Record<string, any> = {}): Promise<InboxResult> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  const token = await u.getIdToken();

  const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  const url = `${apiBase}/community/dm/inbox`;

  try {
    const res = await axios.post(
      url,
      payload || {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return (res.data || {}) as InboxResult;
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const message = data?.detail || data?.error?.message || data?.error?.status || e?.message || (status ? `HTTP ${status}` : 'Request failed');
    throw new Error(String(message));
  }
}

async function callBackendDmListMessages(payload: Record<string, any>): Promise<MessagesResult> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  const token = await u.getIdToken();

  const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  const url = `${apiBase}/community/dm/messages/list`;

  try {
    const res = await axios.post(
      url,
      payload || {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return (res.data || {}) as MessagesResult;
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const message = data?.detail || data?.error?.message || data?.error?.status || e?.message || (status ? `HTTP ${status}` : 'Request failed');
    throw new Error(String(message));
  }
}

async function callBackendDmSendText(payload: Record<string, any>): Promise<void> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  const token = await u.getIdToken();

  const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  const url = `${apiBase}/community/dm/message/send-text`;

  try {
    await axios.post(
      url,
      payload || {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const message = data?.detail || data?.error?.message || data?.error?.status || e?.message || (status ? `HTTP ${status}` : 'Request failed');
    throw new Error(String(message));
  }
}

async function callBackendDmMarkRead(payload: Record<string, any>): Promise<void> {
  const u = auth?.currentUser;
  if (!u) throw new Error('Not authenticated');
  const token = await u.getIdToken();

  const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
  const url = `${apiBase}/community/dm/conversation/mark-read`;

  try {
    await axios.post(
      url,
      payload || {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
  } catch (e: any) {
    const status = e?.response?.status;
    const data = e?.response?.data;
    const message = data?.detail || data?.error?.message || data?.error?.status || e?.message || (status ? `HTTP ${status}` : 'Request failed');
    throw new Error(String(message));
  }
}

function isPermissionDenied(err: any): boolean {
  const m = String(err?.message || '').toLowerCase();
  const code = String(err?.code || '').toLowerCase();
  return code.includes('permission') || m.includes('missing or insufficient permissions') || m.includes('permission-denied') || m.includes('insufficient permissions');
}

export async function listConversationsFallback(limitN = 50): Promise<any[]> {
  const res = await callBackendDmInbox({ limit: limitN });
  return Array.isArray((res as any)?.conversations) ? (res as any).conversations : [];
}

export async function listMessagesFallback(conversationId: string, limitN = 60): Promise<{ conversation: any; messages: any[] }> {
  const res = await callBackendDmListMessages({ conversationId, limit: limitN });
  return {
    conversation: (res as any)?.conversation || null,
    messages: Array.isArray((res as any)?.messages) ? (res as any).messages : [],
  };
}

export async function getOrCreateDirectConversation(otherUid: string): Promise<DirectConversationResult> {
  await ensureFreshIdToken();
  try {
    return await callBackendDmGetOrCreate({ otherUid });
  } catch (be: any) {
    // Se l'endpoint API non esiste (deploy parziale), prova la callable.
    const m = String(be?.message || '').toLowerCase();
    const notFound = m.includes('404') || m.includes('not found');
    const network = m.includes('network error') || m.includes('failed to fetch') || m.includes('timeout');
    if (!notFound && !network) throw be;
  }

  const fn = httpsCallable(functions, 'getOrCreateDirectConversation');
  const res = await fn({ otherUid });
  return res.data as DirectConversationResult;
}

export async function getOrCreateDirectConversationByUserId(otherUserId: string): Promise<DirectConversationResult> {
  await ensureFreshIdToken();
  try {
    return await callBackendDmGetOrCreate({ otherUserId });
  } catch (be: any) {
    const m = String(be?.message || '').toLowerCase();
    const notFound = m.includes('404') || m.includes('not found');
    const network = m.includes('network error') || m.includes('failed to fetch') || m.includes('timeout');
    if (!notFound && !network) throw be;
  }

  const fn = httpsCallable(functions, 'getOrCreateDirectConversation');
  const res = await fn({ otherUserId });
  return res.data as DirectConversationResult;
}

export async function sendTextMessage(conversationId: string, text: string): Promise<void> {
  const uid = requireUid();
  const trimmed = String(text || '').trim();
  if (!trimmed) return;

  try {
    await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
      type: 'text',
      text: trimmed,
      senderId: uid,
      createdAt: serverTimestamp(),
    });
  } catch (e: any) {
    if (isPermissionDenied(e)) {
      await callBackendDmSendText({ conversationId, text: trimmed });
      return;
    }
    throw e;
  }
}

// Usato quando la pagina è in modalità compatibilità (Safari / fallback backend)
export async function sendTextMessageFallback(conversationId: string, text: string): Promise<void> {
  const trimmed = String(text || '').trim();
  if (!trimmed) return;
  await callBackendDmSendText({ conversationId, text: trimmed });
}

export async function markConversationRead(conversationId: string): Promise<void> {
  const uid = requireUid();
  try {
    await updateDoc(doc(db, 'conversations', conversationId), {
      [`readAt.${uid}`]: serverTimestamp(),
      [`unreadCount.${uid}`]: 0,
    });
  } catch (e: any) {
    if (isPermissionDenied(e)) {
      await callBackendDmMarkRead({ conversationId });
      return;
    }
    throw e;
  }
}

export type UploadProgressCb = (pct: number) => void;

function buildStoragePath(conversationId: string, uid: string, messageId: string, fileName: string): string {
  const safeName = fileName.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `conversations/${conversationId}/${uid}/${messageId}/${safeName}`;
}

export async function uploadVoiceMessage(
  conversationId: string,
  blob: Blob,
  opts: { durationMs?: number; mimeType?: string; onProgress?: UploadProgressCb } = {}
): Promise<void> {
  const uid = requireUid();

  const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  const messageId = messageRef.id;

  const mimeType = opts.mimeType || blob.type || 'audio/webm';
  const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'm4a' : 'webm';
  const fileName = `voice.${ext}`;
  const storagePath = buildStoragePath(conversationId, uid, messageId, fileName);

  const storageRef = ref(storage, storagePath);
  const task = uploadBytesResumable(storageRef, blob, { contentType: mimeType });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (opts.onProgress && snap.totalBytes > 0) {
          opts.onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      (err) => reject(err),
      () => resolve()
    );
  });

  const mediaUrl = await getDownloadURL(storageRef);

  await setDoc(messageRef, {
    type: 'voice',
    senderId: uid,
    createdAt: serverTimestamp(),
    mediaUrl,
    storagePath,
    mimeType,
    durationMs: opts.durationMs ?? null,
  });
}

export async function uploadFileMessage(
  conversationId: string,
  file: File,
  opts: { onProgress?: UploadProgressCb } = {}
): Promise<void> {
  const uid = requireUid();

  const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));
  const messageId = messageRef.id;

  const isImage = (file.type || '').startsWith('image/');
  const type = isImage ? 'image' : 'file';
  const storagePath = buildStoragePath(conversationId, uid, messageId, file.name || 'file');

  const storageRef = ref(storage, storagePath);
  const task = uploadBytesResumable(storageRef, file, { contentType: file.type || 'application/octet-stream' });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (opts.onProgress && snap.totalBytes > 0) {
          opts.onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      (err) => reject(err),
      () => resolve()
    );
  });

  const mediaUrl = await getDownloadURL(storageRef);

  await setDoc(messageRef, {
    type,
    senderId: uid,
    createdAt: serverTimestamp(),
    mediaUrl,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    fileName: file.name || null,
    size: file.size || null,
  });
}

export async function archiveConversation(conversationId: string, hidden: boolean): Promise<void> {
  const uid = requireUid();
  await updateDoc(doc(db, 'conversations', conversationId), {
    [`hiddenFor.${uid}`]: Boolean(hidden),
  });
}

export async function bumpUnread(conversationId: string): Promise<void> {
  // Utility (non usata di default): incrementa unread per l'utente corrente
  const uid = requireUid();
  await updateDoc(doc(db, 'conversations', conversationId), {
    [`unreadCount.${uid}`]: increment(1),
  });
}
